import { IconAlertTriangleFilled } from "@tabler/icons-react";
import { type TFunction } from "i18next";

import { EmptyState } from "#app/components/new-ui/empty-state";
import { cn } from "#app/utils/misc.tsx";

const GRACEFUL_STATUSES = new Set([400, 401, 402, 403, 404]);

const STATUS_TITLE_KEYS: Record<number, string> = {
  400: "badRequest",
  401: "unauthorized",
  402: "paymentRequired",
  403: "accessDenied",
  404: "notFound",
};

const STATUS_DESC_KEYS: Record<number, string> = {
  400: "checkInput",
  401: "signInAgain",
  402: "subscriptionRequired",
  403: "noPermission",
  404: "notFoundDescription",
};

function isGracefulHttpStatus(status: number): boolean {
  return GRACEFUL_STATUSES.has(status);
}

export function normalizeRouteErrorData(
  data: unknown,
  t?: TFunction<"errors">,
): string {
  const fallback = t?.("errors:anErrorOccurred") ?? "Something went wrong.";
  if (typeof data === "string") {
    return data.trim() || fallback;
  }
  if (data && typeof data === "object") {
    const rec = data as Record<string, unknown>;
    if (typeof rec.message === "string" && rec.message.trim()) {
      return rec.message.trim();
    }
    if (typeof rec.error === "string" && rec.error.trim()) {
      return rec.error.trim();
    }
    try {
      return JSON.stringify(data);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

export function getHttpErrorPresentation(
  status: number,
  serverMessage: string,
  t?: TFunction<["errors", "common"]>,
): {
  title: string;
  description: string;
  variant: "default" | "destructive";
} {
  const trimmed = serverMessage.trim();
  const titleKey = STATUS_TITLE_KEYS[status];
  const descKey = STATUS_DESC_KEYS[status];
  const description =
    trimmed ||
    (descKey && t ? t(`errors:${descKey}`) : undefined) ||
    (t?.("common:somethingWentWrong") ?? "Something went wrong.");

  if (isGracefulHttpStatus(status)) {
    const title =
      (titleKey && t ? t(`errors:${titleKey}`) : undefined) ??
      "Request could not be completed";
    return { title, description, variant: "default" };
  }

  return {
    title: t
      ? t("errors:somethingWentWrongStatus", { status })
      : `Something went wrong (${status})`,
    description,
    variant: "destructive",
  };
}

export function HttpErrorDisplay({
  title,
  message,
  className,
}: {
  status?: number;
  title: string;
  message: string;
  variant: "default" | "destructive";
  className?: string;
}) {
  return (
    <EmptyState
      title={title}
      description={message}
      icon={
        <IconAlertTriangleFilled
          className="size-8 text-[#141A42] dark:text-zinc-100"
          aria-hidden
        />
      }
      className={cn("min-h-0 w-full py-8", className)}
    />
  );
}
